import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// How long (ms) we keep retrying to reach the saved position while content is loading.
const RESTORE_WINDOW_MS = 2000;
// Throttle interval (ms) for writing scrollY to sessionStorage.
const SAVE_THROTTLE_MS = 100;

const keyFor = (pathname: string, search: string, hash: string) =>
  `scroll:${pathname}${search}${hash}`;

const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();

  // Holds the scrollKey of the *current* page so global listeners always
  // write under the right key, even between effect runs.
  const currentKeyRef = useRef<string>(keyFor(pathname, search, hash));

  // Disable browser native scroll restoration once — we manage it ourselves.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Global save listeners (mount once). Throttled writes + final flush on hide.
  useEffect(() => {
    let lastWrite = 0;
    let pending: number | null = null;

    const flush = () => {
      lastWrite = Date.now();
      pending = null;
      try {
        sessionStorage.setItem(currentKeyRef.current, String(window.scrollY));
      } catch {
        /* ignore quota / private mode errors */
      }
    };

    const onScroll = () => {
      const now = Date.now();
      const elapsed = now - lastWrite;
      if (elapsed >= SAVE_THROTTLE_MS) {
        flush();
      } else if (pending === null) {
        pending = window.setTimeout(flush, SAVE_THROTTLE_MS - elapsed);
      }
    };

    const onPageHide = () => flush();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      if (pending !== null) window.clearTimeout(pending);
    };
  }, []);

  // Per-route navigation handler.
  useEffect(() => {
    const newKey = keyFor(pathname, search, hash);

    if (pathname === "/wiadomosci") {
      currentKeyRef.current = newKey;
      window.scrollTo(0, 0);
      return;
    }

    if (navigationType === "POP") {
      // Back / forward → restore the saved position, retrying while content loads.
      currentKeyRef.current = newKey;
      const saved = sessionStorage.getItem(newKey);
      const target = saved !== null ? Number(saved) : NaN;
      if (Number.isNaN(target) || target <= 0) return;

      const start = performance.now();
      let raf = 0;

      const tick = () => {
        const maxScroll = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight
        );
        // Only scroll if the page is tall enough to actually reach the target.
        if (maxScroll >= target - 1) {
          window.scrollTo(0, target);
          if (Math.abs(window.scrollY - target) <= 1) return;
        }
        if (performance.now() - start < RESTORE_WINDOW_MS) {
          raf = window.requestAnimationFrame(tick);
        }
      };

      raf = window.requestAnimationFrame(tick);
      return () => window.cancelAnimationFrame(raf);
    }

    // PUSH / REPLACE → new page, scroll to top, then start saving under the new key.
    window.scrollTo(0, 0);
    currentKeyRef.current = newKey;
  }, [pathname, search, hash, navigationType]);

  return null;
};

export default ScrollToTop;
